// Read-only presentation of the server's bounded briefSummary projection.
// This does not verify research or accept arbitrary adapter fields as facts.
const text = value => typeof value === 'string' && value.trim() ? value : null
export const RESEARCH_FIELD_LABELS = {teamIdentity: 'Team identity', logoProvenance: 'Official logo provenance', colors: 'Colors', sport: 'Sport', season: 'Season', postseason: 'Postseason'}
export const researchStatusLabel = status => ({verified: 'Source-verified', conflict: 'Conflicting sources', not_applicable: 'Not applicable', unknown: 'Unresolved'}[status] || 'Unresolved')
export function researchAccessDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value) || !Number.isFinite(Date.parse(value))) return null
  return new Date(value).toISOString().replace('T', ' ').replace('.000Z', ' UTC').replace('Z', ' UTC')
}
export function researchSources(summary) {
  if (!Array.isArray(summary?.officialSources) || summary.officialSources.length > 48) return {sources: [], invalid: true}
  const seen = new Set(), sources = []; let invalid = false
  for (const source of summary.officialSources) {
    let url
    try {url = new URL(source.url)} catch {invalid = true; continue}
    const accessed = researchAccessDate(source.accessedAt)
    if (url.protocol !== 'https:' || url.username || url.password || url.hash || !text(source.publisher) || !accessed) {invalid = true; continue}
    const key = JSON.stringify([source.url, source.publisher, source.accessedAt])
    const fields = Array.isArray(source.fields) ? [...new Set(source.fields.filter(field => Object.hasOwn(RESEARCH_FIELD_LABELS, field)))] : []
    if (seen.has(key)) {const prior = sources.find(item => item.key === key); prior.fields = [...new Set([...prior.fields, ...fields])]; continue}
    seen.add(key); sources.push({key, url: source.url, publisher: source.publisher, accessedAt: source.accessedAt, accessed, fields})
  }
  return {sources, invalid}
}
export function researchSummaryFacts(summary) {
  const stringify = {
    teamIdentity: text, sport: text, season: text,
    colors: value => Array.isArray(value) && value.length > 0 && value.length <= 8 && value.every(color => color && text(color.name) && /^#[a-f0-9]{6}$/i.test(color.hex)) ? value.map(color => `${color.name} (${color.hex})`).join(', ') : null,
    postseason: value => {
      if (!value || !['qualification', 'participation', 'title'].includes(value.stage) || !text(value.verifiedAsOf)) return null
      const stage = {qualification: 'Qualification', participation: 'Confirmed participation', title: 'Title'}[value.stage]
      return [stage, text(value.eventName), text(value.eventDate), `Verified as of ${value.verifiedAsOf}`].filter(Boolean).join(' · ')
    },
  }
  return Object.keys(stringify).map(key => {
    const field = summary?.[key], value = field?.status === 'verified' ? stringify[key](field.value) : null
    const status = field?.status === 'verified' && !value ? 'unknown' : field?.status
    return {key, label: RESEARCH_FIELD_LABELS[key], status: researchStatusLabel(status), value: value || (status === 'not_applicable' ? 'Not applicable to this saved intent.' : 'Not established by this research brief.')}
  })
}
export function mayShowResearchSummary(row, advisory = false) {
  return !advisory && Boolean(row?.title && row?.jobId) && !(row.holds || []).some(hold => ['bulk_access_unavailable', 'concept_child_access_changed'].includes(hold.code))
}
